import { countryDialCode } from 'assets/countryDialCode';
import { isMobile as isMobileDetected } from 'react-device-detect';
import { sentryCaptureError } from 'services/sentry/capture';
import { appChannelTypes, FlowTypes } from 'store/constants';
import type { Parameter } from 'store/models';
import {
  PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType as ExtendedTypes,
  PayHubDomainSessionsEnumsPlatformType,
} from 'store/models';

import type { UIPaymentGroups } from '../store/formatters';
import { DEFAULT_THEME_NAME, LayoutType, LOCAL_THEME, NATIVE_PAY_TYPES, THEME_BRAND_MAP } from './constants';
import { stringToInt } from './dataTransform';
import { parseQuery } from './locactionAndUrls';
import { getSessionId } from './session';
import { softLocalStorageGet } from './storage';
import { isValidRegExp } from './validator';

export function inIframe(): boolean {
  try {
    if (window?.top?.location.href === window.location.href) {
      return false;
    }

    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export const getCurrentQueryParams = (): string => window.self.location.search || '';

const doBlur = (): void => {
  const activeElement = document.activeElement as HTMLElement | null;
  activeElement?.blur();
};

let _cachedQueryParams = parseQuery(window.location.search);
export const getCachedQueryParam = (key: string): string => (_cachedQueryParams[key] as string) || '';
export const resetCachedQueryParams = () => {
  _cachedQueryParams = parseQuery(window.location.search);
};

export const resetFocus = (): void => {
  doBlur();

  setTimeout(() => {
    doBlur();
  }, 500);
};

export const isDepositFlowType = (flowType: FlowTypes) => flowType === FlowTypes.deposit;

export const getSubmitCheckoutUrl = (flowType: FlowTypes, paygroupId: string): string => {
  const type = isDepositFlowType(flowType) ? 'payments' : 'payouts';

  return `${type}/groups/${paygroupId}/submit`;
};

export const parseBooleanParam = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (value === '1') return true;
  if (typeof value === 'string') return value.toLowerCase() === 'true';

  return false;
};

export const hasForsedMobileParam = () => {
  const query = parseQuery();
  return parseBooleanParam(query.isForcedMobile);
};

export const getAppChannelFromUrl = () => (_cachedQueryParams.xChannel as appChannelTypes) || appChannelTypes.unknown;

export const getLayoutType = (): LayoutType =>
  isMobileDetected || hasForsedMobileParam() ? LayoutType.mobile : LayoutType.desktop;

export const isMobile = isMobileDetected || hasForsedMobileParam();

export const getFormattedThemeName = (theme?: string): string => {
  if (!theme) {
    return DEFAULT_THEME_NAME;
  }

  const formattedThemeNameAsArray = theme.match(/brand\d+/gm);

  return formattedThemeNameAsArray?.length ? formattedThemeNameAsArray[0] : theme;
};

export const getTheme = (): string => {
  const theme = document.body.dataset.definedTheme;

  return getFormattedThemeName(theme);
};

export const getInitialTheme = (): string => (_cachedQueryParams.theme as string) || '';

export const getMappingChannel = (channel: string, platform?: string) => {
  if (!channel && !platform) return '';
  const isUnknown = channel === appChannelTypes.unknown;

  if (!isUnknown || (isUnknown && !platform)) return channel;

  switch (platform) {
    case PayHubDomainSessionsEnumsPlatformType.Desktop:
      return 'DESKTOP_AIR_PM';

    case PayHubDomainSessionsEnumsPlatformType.Mobile:
      return 'MOBILE_WEB';

    case PayHubDomainSessionsEnumsPlatformType.NativeAndroid:
    case PayHubDomainSessionsEnumsPlatformType.NativeAndroidCasino:
      return 'MOBILE_NATIVE_ANDROID';

    case PayHubDomainSessionsEnumsPlatformType.NativeIos:
    case PayHubDomainSessionsEnumsPlatformType.NativeIosCasino:
      return 'MOBILE_NATIVE_IOS';

    default:
      return platform;
  }
};

export const getThemeByBrand = (brand: string): string => {
  // NOTE: for more info about brands mapping,
  // please go to https://confluence.pm.tech/pages/viewpage.action?spaceKey=PD&title=Design+Tokens
  const mappedBrandTheme = THEME_BRAND_MAP[brand];
  const themeFormQuery = getFormattedThemeName((_cachedQueryParams.theme as string) || DEFAULT_THEME_NAME);

  const storageTheme = softLocalStorageGet(LOCAL_THEME);

  return mappedBrandTheme || themeFormQuery || storageTheme || DEFAULT_THEME_NAME;
};

export const getSubmitButtonVariant = (isValid: boolean) => (isValid ? 'deposit' : 'primary');

export const displayTooltip = (show: boolean) => {
  const modulorTooltip = document.querySelector('[data-component-name="modulor-tooltip"]');
  if (modulorTooltip) {
    if (show) {
      modulorTooltip.classList.add('visibile');
      modulorTooltip.classList.remove('hidden');
    } else {
      modulorTooltip.classList.remove('visibile');
      modulorTooltip.classList.add('hidden');
    }
  }
};

type SetReqHeadersCtx = {
  sessionId?: string;
  sessionIdFromStore?: string;
  endpoint: string;
};

export const setReqHeaders = (headers: Headers, ctx: SetReqHeadersCtx) => {
  const sessionId = ctx.sessionId || getSessionId(ctx.sessionIdFromStore);

  if (!sessionId) {
    // Send to Sentry for monitoring
    sentryCaptureError('SetReqHeaders: no sessionId', {
      loggerId: ctx.endpoint,
      sessionId: '<invalid>',
      extra: {
        href: window?.location?.href,
        pathname: window?.location?.pathname,
        search: window?.location?.search,
        sessionIdFromStore: ctx.sessionIdFromStore,
      },
    });
  }

  // Always set header, even if empty (backend will handle validation)
  headers.set('x-session-id', sessionId || '');
  return headers;
};

export const getRequisiteFieldNameList = (parameters: Parameter[]): string[] => {
  if (!parameters.length) {
    return [];
  }

  return parameters.reduce((acc, current) => {
    if (current.userRequisite) {
      acc.push(current.name);
    }

    return acc;
  }, [] as string[]);
};

export const getGroupAttributes = () => ({
  currencyAttr: document.body.dataset.currency,
  payGroupIdAttr: document.body.dataset.payGroupId ? stringToInt(document.body.dataset.payGroupId) : 0,
});

let _cachedGroupAttributes: ReturnType<typeof getGroupAttributes> | null = null;
export const getCachedGroupAttributes = () => {
  if (!_cachedGroupAttributes) {
    _cachedGroupAttributes = getGroupAttributes();
  }
  return _cachedGroupAttributes;
};

export const findCountryDetailsByPhoneNumber = (number: string) =>
  countryDialCode.find((item) => number.startsWith(`+${item.phonecode}`));

export const findCountryDetailsByCountryISO = (value: string) =>
  countryDialCode.find((country) => value === country.iso);

const _regExpCache = new Map<string, RegExp>();
const _cardExpMonthPattern = /(^[1-9]$)|(^0[1-9]|1[0-2]$)/;
const getCompiledRegExp = (regExp: string) => (isValidRegExp(regExp) ? new RegExp(regExp) : undefined);

export const getPattern = (regExp?: string, extendedType?: ExtendedTypes) => {
  if (!regExp && !extendedType) {
    return undefined;
  }

  if (!regExp && extendedType === ExtendedTypes.CardExpMonth) {
    return _cardExpMonthPattern;
  }

  if (!regExp) {
    return undefined;
  }

  const cacheKey = regExp;
  if (_regExpCache.has(cacheKey)) {
    return _regExpCache.get(cacheKey);
  }

  const compiled = regExp.startsWith('+') ? new RegExp(`\\${regExp}`) : getCompiledRegExp(regExp);

  if (compiled) {
    _regExpCache.set(cacheKey, compiled);
  }

  return compiled;
};

export const getIsRtl = () => document.body.getAttribute('dir') === 'rtl';

export const isEmpty = (value: string | unknown | Record<string, unknown> | null | undefined): boolean => {
  if (value == null) return true;
  if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
  return Object.keys(value).length === 0;
};

export const isMobileDevice = (userAgent: string): { isIOS: boolean; isAndroid: boolean } => {
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);

  return {
    isIOS,
    isAndroid,
  };
};

export const isNativePayAvailable = (nativePayType: string, userAgent?: string, appChannel?: string): boolean => {
  const { isIOS, isAndroid } = userAgent
    ? isMobileDevice(userAgent)
    : {
        isIOS: false,
        isAndroid: false,
      };

  if (nativePayType === NATIVE_PAY_TYPES.APPLE) {
    return isIOS || (!isAndroid && appChannel !== PayHubDomainSessionsEnumsPlatformType.NativeAndroid);
  }

  if (nativePayType === NATIVE_PAY_TYPES.GOOGLE) {
    return isAndroid || (!isIOS && appChannel !== PayHubDomainSessionsEnumsPlatformType.NativeIos);
  }

  return true;
};

export const filterMethodsByPayType = (methodsToFilter: UIPaymentGroups[], userAgent?: string, appChannel?: string) => {
  if (!methodsToFilter?.length) {
    return [];
  }

  const { isIOS, isAndroid } = userAgent
    ? isMobileDevice(userAgent)
    : {
        isIOS: false,
        isAndroid: false,
      };

  const isNativePlatform =
    isIOS ||
    appChannel === PayHubDomainSessionsEnumsPlatformType.NativeIos ||
    isAndroid ||
    appChannel === PayHubDomainSessionsEnumsPlatformType.NativeAndroid;

  if (!isNativePlatform) {
    return methodsToFilter;
  }

  return methodsToFilter.filter(({ nativePayType }) => isNativePayAvailable(nativePayType, userAgent, appChannel));
};
